import type { ActionSummary } from '../../../core/api/types';

/**
 * The four open-action sections of a container page, in their fixed display order.
 *
 * It lives in `shared/actions/model/` rather than under `features/containers/` because 011's tag
 * view presents the same three sections from the same values (research R-006). It depends on
 * `action.status` and nothing else — no container, no kind — which is what makes it shareable at
 * all, and is worth preserving: a section that needed to know which screen it was on would have to
 * move back. `completed` is
 * not among them: completions are the recently-completed list's business (its own request, newest
 * first) and the archive's after that.
 */
export const SECTION_ORDER = ['available', 'deferred', 'blocked', 'on-hold'] as const;

export type SectionStatus = (typeof SECTION_ORDER)[number];

/**
 * FR-019: a section says what it is in words — placement and label, never a hue.
 *
 * Exported because 013's search row needs the same four words for a different reason: a result is
 * grouped by neither container nor status, so the word that a section heading carries everywhere
 * else has to travel on the row. One spelling of "On hold", not two.
 */
export const SECTION_LABELS: Record<SectionStatus, string> = {
  available: 'Available',
  deferred: 'Deferred',
  blocked: 'Blocked',
  'on-hold': 'On hold',
};

export interface ActionSection {
  readonly status: SectionStatus;
  readonly label: string;
  /** In the order the API sent them (FR-017); never re-sorted. */
  readonly actions: readonly ActionSummary[];
}

/**
 * Partition actions into the four sections, reading **`action.status` alone** (research R-003).
 * The server has already collapsed dueAt, deferUntil, blockers and container state into that one
 * value with the precedence completed > on-hold > blocked > deferred > available; re-deriving any
 * of it here would disagree with the server on exactly the overlapping cases the facet exists to
 * settle. Each action lands in at most one section; empty sections are omitted.
 */
export function sectionsOf(actions: readonly ActionSummary[]): ActionSection[] {
  const buckets: Record<SectionStatus, ActionSummary[]> = {
    available: [], deferred: [], blocked: [], 'on-hold': [],
  };

  for (const action of actions) {
    switch (action.status) {
      case 'available':
        buckets.available.push(action);
        break;
      case 'deferred':
        buckets.deferred.push(action);
        break;
      case 'blocked':
        buckets.blocked.push(action);
        break;
      case 'on-hold':
        buckets['on-hold'].push(action);
        break;
      case 'completed':
        // Not a section: shown by the recently-completed list instead.
        break;
      default: {
        // The `never` keeps this exhaustive at compile time. At runtime the only way here is deploy
        // skew — an API that has learned a sixth status this build does not know — and T076's point
        // is that throwing inside a computed blanks the whole container page rather than degrading
        // the one row. Available is where an action the client cannot classify is still workable.
        const unhandled: never = action.status;
        void unhandled;
        buckets.available.push(action);
        break;
      }
    }
  }

  return SECTION_ORDER
    .map((status) => ({ status, label: SECTION_LABELS[status], actions: buckets[status] }))
    .filter((section) => section.actions.length > 0);
}
